import React, { useEffect, useState } from "react";
import {
  Box,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { NoteData } from "../../../utils/types";
import useDisplaySnackbar from "../../../utils/useDisplaySnackbar";
import ProgressSpinner from "../../Common/ProgressSpinner";
import { getUserAttributes } from "../../../utils/auth";
import RichTextEditor from "../../Common/RichTextEditor";
import "../../../styles/common.css"
import NotesService from "../../../services/notes/NoteService";
interface NoteProps {
  note?: NoteData | null;
  isPrivate: boolean;
  isEditMode: boolean;
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const Note: React.FC<NoteProps> = ({
  note,
  isPrivate,
  isEditMode,
  onExpandChange,
}) => {
  const { showSnackbar } = useDisplaySnackbar();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [editedContent, setEditedContent] = useState(note?.notes);
  const [noteId, setNoteId] = useState(note?.id || "");
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const loggedInUserEmail = getUserAttributes()?.email;
  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  const { mutate: mutateNoteCreate } = NotesService.useCreateNoteMutation(
    {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get_notes"],
        });
        showSnackbar("success", "Note created successfully");
      },
      onError: (error: string) => {
        showSnackbar("error", `Error while creating note: ${error}`);
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    }
  );

  useEffect(() => {
    if (note) {
      setEditedContent(note?.notes);
      setNoteId(note?.id);
      setIsEditable(
        note.creatorEmail === loggedInUserEmail && isEditMode
      );
    } else {
      setEditedContent("");
      setNoteId("");
      setIsEditable(true);
    }
  }, [note]);

  const handleSave = () => {
    if (!editedContent) return;
    mutateNoteCreate({
      interactionId: id || "",
      text: editedContent,
      isPrivate,
    });
    if (onExpandChange) onExpandChange(false);
  };

  const handleCancel = () => {
    setEditedContent(note?.notes || "");
    if (onExpandChange) onExpandChange(false);
  };
  const isContentEmpty = !editedContent || editedContent.replace(/<[^>]*>/g, "").trim() === "";

  return (
    <>
        <Box sx={{height: "280px", width: "100%"}}>
          <div className="meetingNotesRichTextEditor">
            <RichTextEditor
              key={noteId || "new_note"}
              placeholder="Take notes"
              content={editedContent}
              isEditable={isEditable}
              updateOnState={true}
              onContentUpdate={(content) => setEditedContent(content)}
              parentPage="idp-add-note"
              height="400px"
              showFooter={true}
              autoFocus={true}
              footer={
                <>
                <button
                  className={`btn btn-purple-compact ${isContentEmpty ? "disabled-look" : ""}`}
                  onClick={() => handleSave()}
                  disabled={isContentEmpty}
                >
                  Save
                </button>
                <button
                  className="btn btn-outlined-small"
                  onClick={() => {
                    handleCancel();
                  }}
                >
                  Cancel
                </button>
                </>
              }
            />
            </div>
        </Box>
    <ProgressSpinner showSpinner={showSpinner}/>
    </>
  );
};

export default Note;
